import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useClientId } from "@/shared/hooks/useClientId";
import { useIsDemoUser } from "@/shared/hooks/useIsDemoUser";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useDocumentTitle, buildPageTitle } from "@/shared/hooks/useDocumentTitle";
import { useAssetById } from "@/features/assets/hooks/useAssetById";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { AssetDetailContent } from "@/features/assets/components/asset-registry/assetDetailPanel/AssetDetailContent";
import { AssetHeroHeader } from "@/features/assets/components/asset-registry/assetDetailPanel/AssetHeroHeader";
import { ClientAssetDetailSkeleton } from "@/features/assets/components/ClientAssetDetailSkeleton";
import { EditAssetModal } from "@/features/assets/components/EditAssetModal";
import { assetDetailStyles } from "@/features/assets/services/assetDetailStyles";
import { getQrCodeImageUrl } from "@/features/assets/services/qrCodeUrl";
import {
  ChevronRight,
  Pencil,
  X,
  Download,
  FileSpreadsheet,
  FileJson,
  Loader2,
  Package,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { exportSingleAsset, type SingleAssetExportFormat } from "@/features/exports/services";
import { useExportsEnabled } from "@/shared/hooks/useExportsEnabled";
import { toast } from "@/shared/hooks/use-toast";


export default function ClientAssetDetail() {
  const params = useParams<{
    propertyId: string;
    assetId: string;
  }>();
  const { propertyId, assetId: assetIdParam } = params;
  const { clientId } = useClientId();
  const navigate = useNavigate();
  const { projects, loading: projectsLoading } = useProjects();
  const assetId = assetIdParam ?? null;
  const { data: asset, isLoading: assetLoading, refetch } = useAssetById(assetId);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<SingleAssetExportFormat | null>(null);
  const { enabled: exportsEnabled } = useExportsEnabled(clientId);
  const isDemoUser = useIsDemoUser();

  const handleAssetExport = async (format: SingleAssetExportFormat) => {
    if (!assetId) return;
    setExportingFormat(format);
    try {
      await exportSingleAsset({ assetId, projectId: clientId ?? undefined, format });
      toast({ title: `Asset exported as ${format.toUpperCase()}` });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setExportingFormat(null);
    }
  };

  // Get client and property data
  const { client, property } = useMemo(() => {
    const project = projects.find(p => p.id === clientId);
    if (!project) return { client: null, property: null };

    const propertyData = project.properties.find(p => p.id === propertyId);
    if (!propertyData) return { client: null, property: null };

    return {
      client: {
        id: project.id,
        name: project.name,
      },
      property: {
        id: propertyData.id,
        name: propertyData.name,
      }
    };
  }, [projects, clientId, propertyId]);

  useDocumentTitle(
    client && property && asset
      ? buildPageTitle([asset.name, property.name, client.name], "| Asset Detail")
      : null
  );

  // Convert asset to EditAssetModal format - MUST be before conditional returns
  const assetForEdit = useMemo(() => {
    if (!asset) return null;
    return {
      id: asset.id,
      name: asset.name,
      asset_type: asset.asset_type || asset.category || "",
      serial_number: asset.serial_number,
      model_number: asset.model_number,
      manufacturer: asset.manufacturer,
      property_id: asset.property_id ?? undefined,
      status: (asset.status as "Active" | "Out-of-Service" | "Maintenance" | "Decommissioned") || "Active",
      notes: asset.notes ?? undefined,
      condition: asset.condition,
      installation_date: asset.installation_date,
      category_id: asset.category_id,
      category_attributes: asset.category_attributes,
    };
  }, [asset]);

  // Loading state
  if (projectsLoading || assetLoading) {
    return <ClientAssetDetailSkeleton />;
  }

  // Not found state (require assetId so documents/photos fetch and tabs get valid id;
  // reject assets that don't belong to the property in the URL)
  if (!client || !property || !asset || !assetId || asset.property_id !== propertyId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/20">
        <div className="flex items-center justify-center h-screen">
          <Card className="w-full max-w-md p-6 text-center">
            <CardHeader>
              <CardTitle>Asset Not Found</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                The asset you're looking for could not be found.
              </p>
              <Button onClick={() => navigate("/dashboard")}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={assetDetailStyles.pageBackground}>
      <div className="flex h-screen overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Compact Header */}
          <div className={cn(assetDetailStyles.compactHeader, "sticky top-0 z-10 relative")}>
            {/* Decorative overlay */}
            <div className={assetDetailStyles.decorativeOverlay} />

            <div className="px-6 py-4 relative z-10">
              {/* Breadcrumbs with close button */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="hover:text-foreground transition-colors"
                  >
                    {client.name}
                  </button>
                  <ChevronRight className="h-4 w-4" />
                  <button
                    onClick={() => navigate(`/property/${propertyId}`)}
                    className="hover:text-foreground transition-colors"
                  >
                    {property.name}
                  </button>
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-foreground font-medium">
                    {asset.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {exportsEnabled && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={exportingFormat !== null}
                          className="gap-2"
                        >
                          {exportingFormat !== null ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          Export
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleAssetExport("xlsx")}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" /> XLSX
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleAssetExport("csv")}>
                          <Download className="h-4 w-4 mr-2" /> CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleAssetExport("json")}>
                          <FileJson className="h-4 w-4 mr-2" /> JSON (metadata only)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleAssetExport("zip")}>
                          <Package className="h-4 w-4 mr-2" /> ZIP (with photos + documents)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {!isDemoUser && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditModalOpen(true)}
                      className="hover:bg-accent/20 hover:shadow-[0_0_15px_rgba(245,158,11,0.2)] transition-all"
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Asset
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/property/${propertyId}`)}
                    className="hover:bg-accent/20 hover:shadow-[0_0_15px_rgba(245,158,11,0.2)] transition-all"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content — same sections as the asset drawer */}
          <ScrollArea className="flex-1">
            <AssetHeroHeader
              asset={asset}
              qrImageUrl={getQrCodeImageUrl(asset.qr_code_id)}
              isTransitioning={false}
            />
            <div className="p-6">
              <AssetDetailContent asset={asset} />
            </div>
          </ScrollArea>
        </div>

      </div>

      {/* Edit Asset Modal */}
      {assetForEdit && (
        <EditAssetModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          asset={assetForEdit}
          onAssetUpdated={refetch}
        />
      )}
    </div>
  );
}
